import { ChevronDown } from 'lucide-react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarLinkConfig } from './sidebarConfig';
import { AnimatedCollapsible } from '../../common';

// ----------------------------------------------------------------
// Accordion context — shared across all SidebarItems so that
// opening one parent automatically closes all other parents.
// ----------------------------------------------------------------

interface AccordionCtx {
  openId: string | null;
  toggle: (id: string) => void;
  forceOpen: (id: string) => void;
}

export const SidebarAccordionContext = createContext<AccordionCtx>({
  openId: null,
  toggle: () => {},
  forceOpen: () => {},
});

export const SidebarAccordionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = useCallback(
    (id: string) => setOpenId((prev) => (prev === id ? null : id)),
    [],
  );
  const forceOpen = useCallback((id: string) => setOpenId(id), []);
  const value = useMemo(
    () => ({ openId, toggle, forceOpen }),
    [openId, toggle, forceOpen],
  );
  return (
    <SidebarAccordionContext.Provider value={value}>
      {children}
    </SidebarAccordionContext.Provider>
  );
};

// ----------------------------------------------------------------
// SidebarItem
// ----------------------------------------------------------------

interface SidebarItemProps {
  config: SidebarLinkConfig;
  isCollapsed: boolean;
  /** Set by a parent so only its most specific matching child is active. */
  isActiveOverride?: boolean;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  config,
  isCollapsed,
  isActiveOverride,
}) => {
  const { openId, toggle, forceOpen } = useContext(SidebarAccordionContext);
  const location = useLocation();
  const hasChildren = Boolean(config.children?.length);
  const IconComponent = config.icon;

  const matchesRoute = (
    to?: string,
    activeMatch: 'exact' | 'prefix' = 'prefix',
  ) =>
    !!to &&
    (activeMatch === 'exact'
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`));

  const isActive =
    isActiveOverride ?? matchesRoute(config.to, config.activeMatch);
  // When sibling paths nest (e.g. /bookings and /bookings/new), only the
  // longest matching one is active.
  const activeChildId = hasChildren
    ? config.children
        ?.filter((child) => matchesRoute(child.to, child.activeMatch))
        .reduce<SidebarLinkConfig | null>(
          (best, child) =>
            !best || (child.to?.length ?? 0) > (best.to?.length ?? 0)
              ? child
              : best,
          null,
        )?.id
    : undefined;
  const isChildActive = Boolean(activeChildId);
  const isOpen = openId === config.id || !!isChildActive;

  // Auto-open when a child route is active
  useEffect(() => {
    if (isChildActive) forceOpen(config.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChildActive, config.id]);

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          type="button"
          onClick={() => toggle(config.id)}
          title={isCollapsed ? config.label : undefined}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
            isOpen || isChildActive
              ? 'bg-pos-light/30 text-pos-primary'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center gap-3">
            {IconComponent && (
              <IconComponent size={20} className="flex-shrink-0" />
            )}
            {!isCollapsed && (
              <span className="text-sm font-medium">{config.label}</span>
            )}
          </div>
          {!isCollapsed && (
            <span
              className={`transition-transform duration-300 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
            >
              <ChevronDown size={16} />
            </span>
          )}
        </button>

        {/* Animated children panel */}
        <AnimatedCollapsible isOpen={isOpen && !isCollapsed}>
          <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-4 pb-1">
            {config.children?.map((child) => (
              <SidebarItem
                key={child.id}
                config={child}
                isCollapsed={isCollapsed}
                isActiveOverride={
                  child.children?.length
                    ? undefined
                    : child.id === activeChildId
                }
              />
            ))}
          </div>
        </AnimatedCollapsible>
      </div>
    );
  }

  return (
    <div className="mb-1">
      <Link
        to={config.to || '#'}
        title={isCollapsed ? config.label : undefined}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
          isActive
            ? 'bg-pos-primary text-white'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        {IconComponent && <IconComponent size={20} className="flex-shrink-0" />}
        {!isCollapsed && (
          <span className="text-sm font-medium">{config.label}</span>
        )}
      </Link>
    </div>
  );
};
